
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { addTransaction, updateChild } from "@/lib/firebase";
import { AddTransactionForm } from "@/components/transactions/AddTransactionForm";
import { ChildHeader } from "@/components/children/ChildHeader";
import { ChildBalance } from "@/components/children/ChildBalance";
import { TransactionTabs } from "@/components/transactions/TransactionTabs";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useChildData } from "@/hooks/useChildData";

export default function ChildDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const {
    child,
    transactions,
    isLoading,
    fetchTransactions,
    refreshChildData
  } = useChildData(id);
  
  console.log("ChildDetailPage - URL param id:", id);
  console.log("ChildDetailPage - currentUser:", currentUser);
  
  const isParent = currentUser?.role === 'parent';
  
  const handleAddTransaction = async (amount: number, description: string) => {
    if (!id) return;
    
    try {
      await addTransaction(id, amount, description);
      
      // Refresh transaction data
      await fetchTransactions();
      
      // Refresh child data to get updated balance
      await refreshChildData();
    } catch (error) {
      console.error("Error adding transaction:", error);
      toast({
        title: "Error",
        description: "Failed to add transaction. Please try again.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleUpdateChild = async (childId: string, firstName: string, lastName: string) => {
    try {
      console.log("Updating child:", childId, firstName, lastName);
      await updateChild(childId, firstName, lastName);
      
      // Refresh child data
      await refreshChildData();
      
      toast({
        title: "Success!",
        description: "Child information updated successfully.",
      });
    } catch (error) {
      console.error("Error updating child:", error);
      toast({
        title: "Error",
        description: "Failed to update child information. Please try again.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleTransactionDeleted = async () => {
    // Refresh child data to get updated balance
    await refreshChildData();
    
    // Refresh transaction list
    await fetchTransactions();
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-shekel-600"></div>
      </div>
    );
  }
  
  if (!child) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Child not found</p>
        <Button onClick={() => navigate("/")} className="mt-4">
          Back to Dashboard
        </Button>
      </div>
    );
  }
  
  return (
    <div>
      <Button
        variant="ghost"
        onClick={() => navigate("/")}
        className="mb-4 pl-0 flex items-center"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Dashboard
      </Button>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <ChildHeader 
          child={child} 
          isParent={isParent} 
          onUpdateChild={handleUpdateChild} 
        />
        <ChildBalance child={child} />
      </div>
      
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold">Transactions</h2>
        {isParent && (
          <AddTransactionForm
            onAddTransaction={handleAddTransaction}
            childName={child.firstName}
          />
        )}
      </div>
      
      <TransactionTabs
        transactions={transactions}
        isAdmin={isParent}
        onTransactionDeleted={handleTransactionDeleted}
      />
    </div>
  );
}
